import React, { useState } from "react";
import {
    Button,
    Checkbox,
    Drawer,
    Form,
    Input,
    Row,
    Space,
    Tooltip,
    Typography,
    Image,
} from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Supplier } from "../../../types/SupplierTypes";
import { createSupplierBrief } from "../../../common/api/SupplierClient";
import { handleError } from "../../../common/ErrorHandling";
import { FullPanelSpin } from "./components/FullPanelSpin";
import extractDomain from "extract-domain";

interface SupplierGeneratorPanelProps {
    open?: boolean;
    alwaysShow?: boolean;
    onClose?: () => void;
    onCreated?: () => void;
    onAlwaysShowChange?: (checked: boolean) => void;
}

export const SupplierGenratorPanel: React.FC<SupplierGeneratorPanelProps> = ({
    open,
    alwaysShow,
    onClose,
    onCreated,
    onAlwaysShowChange,
}) => {
    const [supplier, setSupplier] = useState({} as Supplier);
    const [showSpinner, setShowSpinner] = useState(false);

    const generateButtonDisabled = !supplier.name || !supplier.website;

    const addSupplier = async () => {
        try {
            setShowSpinner(true);
            await createSupplierBrief(supplier);
            onCreated?.();
        } catch (error) {
            handleError(error);
        } finally {
            setShowSpinner(false);
        }
    };

    const namePrefix = supplier.logo ? (
        <div style={{ width: 20 }}>
            <Image src={supplier.logo} />
        </div>
    ) : null;

    const content = (
        <div>
            <Typography.Title level={5} style={{ fontWeight: 400 }}>
                Enter a company name and website in order to create your Buyer brief.
            </Typography.Title>
            <Form layout="vertical">
                <Form.Item label="Company name">
                    <Input
                        value={supplier.name}
                        placeholder="Enter..."
                        prefix={namePrefix}
                        onChange={(e) => {
                            setSupplier({
                                ...supplier,
                                name: e.target.value,
                            });
                        }}
                    />
                </Form.Item>
                <Form.Item label="Website">
                    <Input
                        value={supplier.website}
                        placeholder="Enter..."
                        onChange={(e) => {
                            setSupplier({
                                ...supplier,
                                website:
                                    extractDomain(e.target.value)?.toString() ?? e.target.value,
                            });
                        }}
                    />
                </Form.Item>
                <Form.Item>
                    <Checkbox
                        checked={alwaysShow}
                        onChange={(e) => onAlwaysShowChange?.(e.target.checked)}
                    >
                        Always show Buyer brief generator.
                    </Checkbox>
                </Form.Item>
            </Form>
        </div>
    );
    return (
        <Drawer
            title="New Buyer brief"
            placement="right"
            width={380}
            open={open}
            onClose={onClose}
            extra={
                <Tooltip
                    title={
                        "Conduct comprehensive due diligence on any company worldwide, whether publicly traded or private, using advanced AI insights."
                    }
                    placement="left"
                >
                    <ExclamationCircleOutlined />
                </Tooltip>
            }
            footer={
                <Row justify="end">
                    <Space>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button
                            type="primary"
                            disabled={generateButtonDisabled}
                            onClick={addSupplier}
                        >
                            Generate Buyer brief
                        </Button>
                    </Space>
                </Row>
            }
        >
            {showSpinner && <FullPanelSpin />}
            {content}
        </Drawer>
    );
};
